import { MONTHS, OBJECTIVES_ERRORS, SCOPES } from "constants/constants";
import { Typography } from "core/typography/Typography";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import colors from "assets/styles/mixins/colors.scss";

export const getTypes = ({ objectives, objAndRealizedTypes }) => {
  if (objectives?.length === 0) {
    return [
      {
        label: { id: "initials", defaultMessage: "Initiaux" },
        value: 0,
      },
    ];
  } else {
    return objAndRealizedTypes?.map((type) => ({
      value: type?.value,
      label: type?.label,
    }));
  }
};

export const getYearsDropdown = (year) =>
  Array.from({ length: 5 }, (_, i) => ({
    value: year - 2 + i,
    label: `${year - 2 + i}`,
  }));

const createHeaderItem = (key, id, defaultMessage) => ({
  label: {
    key,
    value: defaultMessage ? (
      <FormattedMessage id={id} defaultMessage={defaultMessage} />
    ) : (
      ""
    ),
  },
  id,
});

export const headerItems = [
  createHeaderItem("type", "objectives_type", "Type d'objectifs"),
  createHeaderItem("concerned_user", "recipient", "Destinataire"),
  createHeaderItem("year", "year", "Année"),
  createHeaderItem("objectives", "objectives", "Objectifs"),
  createHeaderItem("update_by", "updated_by", "Modifié par"),
  createHeaderItem("date", "updated_at", "Le"),
  createHeaderItem("delete", "delete", ""),
];

export const getUserName = (user) => {
  return `${user?.first_name} ${user?.last_name}`;
};

export const Equal = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const formatPayloadId = (data) => {
  const formattedData = {
    ids: [...data.map((elm) => elm?.objectives.id)],
  };
  return formattedData;
};

export const formatPayload = (data) => {
  const formattedData = data.map((item) => ({
    concerned_user: item.objectives.concerned_user,
    type: item.objectives?.type,
    year: item.objectives?.year,
    january: item.objectives?.january,
    february: item.objectives?.february,
    march: item.objectives?.march,
    april: item.objectives?.april,
    may: item.objectives?.may,
    june: item.objectives?.june,
    july: item.objectives?.july,
    august: item.objectives?.august,
    september: item.objectives?.september,
    october: item.objectives?.october,
    november: item.objectives?.november,
    december: item.objectives?.december,
    id: item?.objectives?.id || null,
  }));

  return formattedData;
};

const validateMonthlyData = (objectivesArray, setError) => {
  for (const obj of objectivesArray) {
    const values = Object.values(obj.objectives);
    const hasEmptyValue = values.some(
      (value) => value === null || value === undefined || value === ""
    );
    if (hasEmptyValue) {
      setError(OBJECTIVES_ERRORS.ERROR_TYPE_MISSING_MONTHLY_DATA);
      return false;
    }
  }
  return true;
};

export const userAndObjTypesArray = (objectivesArray) => {
  return objectivesArray?.map((obj) => ({
    concerned_user: obj.objectives?.concerned_user || obj.concerned_user,
    type: obj?.objectives?.type,
    year: obj?.objectives?.year,
  }));
};

const validateNewObjectives = (newObjectives, existingObjectives, setError) => {
  const existingUserObjectives = userAndObjTypesArray(existingObjectives);
  const combinedObjectives = [
    ...existingUserObjectives,
    ...userAndObjTypesArray(newObjectives),
  ];

  for (const newObj of newObjectives) {
    const concernedUserId = newObj.objectives.concerned_user;
    const year = newObj?.objectives?.year || newObj?.year;
    const type = newObj?.objectives?.type;

    const userObjectivesForYear = combinedObjectives.filter(
      (obj) => obj.concerned_user === concernedUserId && obj.year === year
    );

    const typeCounts = countObjectiveTypes(userObjectivesForYear);

    if (hasMultipleObjectivesOfType(typeCounts)) {
      setError(OBJECTIVES_ERRORS.ERROR_MULTIPLE_OBJECTIVES);
      return false;
    }

    if (
      isReforecastObjectiveWithoutInitial(type, userObjectivesForYear, year)
    ) {
      setError(OBJECTIVES_ERRORS.ERROR_TYPE_MISMATCH);
      return false;
    }
  }

  return true;
};

const countObjectiveTypes = (objectives) => {
  return objectives.reduce((counts, obj) => {
    counts[obj.type] = (counts[obj.type] || 0) + 1;
    return counts;
  }, {});
};

const hasMultipleObjectivesOfType = (typeCounts) => {
  return typeCounts[0] > 1 || typeCounts[1] > 1;
};

const isReforecastObjectiveWithoutInitial = (type, userObjectives, year) => {
  return (
    type === 1 &&
    !userObjectives.some((obj) => obj.type === 0 && obj.year === year)
  );
};

const getUpdatedObjectives = (initialObjectives, objectivesArray) => {
  return objectivesArray.filter((obj) => {
    const initialObj = initialObjectives.find(
      (initObj) => initObj.objectives.id === obj.objectives.id
    );
    return initialObj && !Equal(initialObj.objectives, obj.objectives);
  });
};

const validateYearPresence = (objectives, setError) => {
  for (const obj of objectives) {
    if (
      !obj?.objectives?.year &&
      obj?.objectives?.user_concerned &&
      obj?.objectives?.type
    ) {
      setError(OBJECTIVES_ERRORS.ERROR_TYPE_MISSING_YEAR);
      return false;
    }
  }
  return true;
};

export const hasType1ObjectiveForYearAndUser = (
  objectives,
  year,
  concernedUser
) => {
  return objectives.some(
    (obj) =>
      obj.objectives?.type === 1 &&
      obj.objectives?.year === year &&
      obj.objectives?.concerned_user === concernedUser &&
      !obj.newRow
  );
};

export const onChange = ({
  id,
  key,
  setData,
  data,
  val,
  setUpdatedObjectives,
  initialObjectives,
  setNewObjectives,
}) => {
  const updatedData = data.map((elm) => {
    const elmId = elm.newRow ? elm.id : elm.objectives.id;
    if (elmId === id) {
      const updatedValue = val[0]?.value || val;
      const updatedElm = { [key]: updatedValue };
      return { ...elm, objectives: { ...elm.objectives, ...updatedElm } };
    } else {
      return elm;
    }
  });
  const isModified = (initialObj, updatedObj) => {
    const keys = Object.keys(initialObj).filter(
      (key) => key !== "created_at" && key !== "updated_at"
    );
    return keys.some((key) => {
      return initialObj[key] !== updatedObj[key];
    });
  };

  const modifiedObjectives = updatedData.filter((elm) => {
    if (elm.newRow) return false;
    const initialObj = initialObjectives.find(
      (initObj) => initObj.objectives.id === elm.objectives.id
    );
    return initialObj && isModified(initialObj.objectives, elm.objectives);
  });

  const newObject = updatedData.filter((elm) => elm.newRow === true);
  setData(updatedData);
  setUpdatedObjectives(modifiedObjectives);
  if (newObject.length > 0) {
    setNewObjectives(newObject);
  }
};

export const onAdd = ({ header, data, setData, defaultYear }) => {
  const newId = _.uniqueId();

  const keys = ["id", ...header.map((elm) => elm?.label?.key)];

  const objectivesTemplate = MONTHS.reduce((acc, month) => {
    acc[month.toLowerCase()] = null;
    return acc;
  }, {});

  const newData = keys.reduce(
    (prev, curr) => {
      let value;

      if (curr === "id") {
        value = newId;
      } else if (curr === "objectives") {
        value = objectivesTemplate;
      } else if (curr === "year") {
        value = defaultYear;
      } else {
        value = null;
      }

      return {
        ...prev,
        [curr]: value,
      };
    },
    { newRow: true }
  );

  setData([...data, newData]);
};

export const onDelete = ({
  id,
  data,
  setData,
  getUpdatedValues,
  setElementsToDelete,
  newObjectives,
  setNewObjectives,
}) => {
  const updatedData = data.filter(
    (elm) => elm.objectives.id !== id && elm.id !== id
  );
  const elementToDelete = data.find((elm) => elm.objectives.id === id);
  if (elementToDelete) {
    setElementsToDelete((prevElementsToDelete) => [
      ...prevElementsToDelete,
      elementToDelete,
    ]);
  }
  const updatedNewObjectives = newObjectives.filter(
    (obj) => obj.id !== id && obj.newRow
  );
  setNewObjectives(updatedNewObjectives);
  setData(updatedData);
  return getUpdatedValues(updatedData);
};

export const onChangeTable = ({
  id,
  key,
  val,
  setData,
  data,
  setUpdatedObjectives,
  setNewObjectives,
  initialObjectives,
}) => {
  const updatedData = data.map((elm) => {
    const elmId = elm.newRow ? elm.id : elm.objectives.id;

    if (elmId === id) {
      const updatedObjectives = { ...elm.objectives, [key]: val };
      return { ...elm, objectives: updatedObjectives };
    } else {
      return elm;
    }
  });
  const isModified = (initialObj, updatedObj) => {
    const keys = Object.keys(initialObj).filter(
      (key) => key !== "created_at" && key !== "updated_at"
    );
    return keys.some((key) => {
      return initialObj[key] !== updatedObj[key];
    });
  };
  const modifiedObjectives = updatedData.filter((elm) => {
    if (elm.newRow) return false;
    const initialObj = initialObjectives.find(
      (initObj) => initObj.objectives.id === elm.objectives.id
    );
    return initialObj && isModified(initialObj.objectives, elm.objectives);
  });

  setData(updatedData);
  setUpdatedObjectives(modifiedObjectives);
  setData(updatedData);
  const newObject = updatedData.filter((elm) => elm.newRow === true);
  if (newObject.length > 0) {
    setNewObjectives(newObject);
  }
};

const checkAndCloseModal = (objectivesArray, initialObjectives, closeModal) => {
  if (
    objectivesArray.length === 0 ||
    Equal(objectivesArray, initialObjectives)
  ) {
    closeModal();
  }
};

const resetStateAfterOperation = (
  setObjectivesArray,
  updatedObjectivesArray,
  setNewObjectives,
  updatedNewObjectives
) => {
  setObjectivesArray(updatedObjectivesArray);
  setNewObjectives(updatedNewObjectives);
};

const validateAllData = (
  updatedObjectivesArray,
  updatedNewObjectives,
  setError
) => {
  if (!validateMonthlyData(updatedObjectivesArray, setError)) return false;
  if (!validateYearPresence(updatedNewObjectives, setError)) return false;
  return true;
};
export const validateTables = async ({
  initialObjectives,
  objectivesArray,
  setObjectivesArray,
  setRefresh,
  createObjectives,
  editObjectives,
  deleteObjectives,
  newObjectives,
  setNewObjectives,
  setElementsToDelete,
  setError,
  elementsToDelete,
  closeModal,
}) => {
  try {
    setError(null);
    setRefresh(false);

    const filteredObjectivesArray = filterIncompleteDrafts(objectivesArray);
    const filteredNewObjectives = filterIncompleteDrafts(newObjectives);

    let updatedObjectivesArray = [...filteredObjectivesArray];
    let updatedNewObjectives = [...filteredNewObjectives];

    if (elementsToDelete.length > 0) {
      const validDeletions = filterValidDeletions(
        setElementsToDelete,
        elementsToDelete,
        initialObjectives,
        setError
      );

      if (validDeletions.length > 0) {
        await deleteObjectives(formatPayloadId(validDeletions));
        updatedObjectivesArray = updateObjectivesArray(
          filteredObjectivesArray,
          filteredNewObjectives
        ).objectivesArray;
      } else {
        setElementsToDelete([]);
        setRefresh(true);
        return;
      }
    }

    const updatedObjectivesValues = getUpdatedObjectives(
      initialObjectives,
      updatedObjectivesArray
    );
    if (updatedObjectivesValues.length > 0) {
      await editObjectives(formatPayload(updatedObjectivesValues));
      updatedObjectivesArray = updateObjectivesArray(
        filteredObjectivesArray,
        filteredNewObjectives
      ).objectivesArray;
      initialObjectives = [...updatedObjectivesArray];
      setRefresh(true);
    }

    if (updatedNewObjectives.length > 0) {
      if (
        !validateNewObjectives(
          updatedNewObjectives,
          initialObjectives,
          setError
        )
      )
        return;
      if (
        !validateAllData(updatedObjectivesArray, updatedNewObjectives, setError)
      )
        return;

      await createObjectives(formatPayload(updatedNewObjectives));
      updatedObjectivesArray = filterObjectivesArray(updatedObjectivesArray);
      updatedNewObjectives = [];

      resetStateAfterOperation(
        setObjectivesArray,
        updatedObjectivesArray,
        setNewObjectives,
        updatedNewObjectives
      );
    }

    setRefresh(true);
    closeModal();
  } catch (error) {
    checkAndCloseModal(objectivesArray, initialObjectives, closeModal);
    setError("missing_data_objective_error");
  }
};

const filterIncompleteDrafts = (objectives) => {
  return objectives.filter((obj) => {
    return obj.objectives.concerned_user && obj.objectives.type !== null;
  });
};

const updateObjectivesArray = (objectivesArray, newObjectives) => {
  const updatedObjectivesArray = filterObjectivesArray(objectivesArray);
  const updatedNewObjectives = filterNewObjectives(newObjectives, []);
  return {
    objectivesArray: updatedObjectivesArray,
    newObjectives: updatedNewObjectives,
  };
};

const filterValidDeletions = (
  setElementsToDelete,
  elementsToDelete,
  initialObjectives,
  setError
) => {
  return elementsToDelete.filter((obj) => {
    const year = obj.objectives?.year;
    const concernedUser = obj.objectives?.concerned_user;
    const hasType1 = hasType1ObjectiveForYearAndUser(
      initialObjectives,
      year,
      concernedUser
    );

    if (obj.objectives?.type === 0 && hasType1) {
      setError(OBJECTIVES_ERRORS.ERROR_TYPE_INIT_OBJECTIVE_REFORCASTED);
      setElementsToDelete([]);
      return false;
    }

    const existsInInitial = initialObjectives.some(
      (initObj) => initObj.objectives?.id === obj.objectives?.id
    );
    return existsInInitial;
  });
};

const filterNewObjectives = (newObjectives, elementsToDelete) => {
  return newObjectives.filter(
    (obj) =>
      !elementsToDelete.some(
        (delObj) => delObj.objectives.id === obj.objectives.id
      )
  );
};

const filterObjectivesArray = (objectivesArray) => {
  return objectivesArray.filter(
    (obj) => obj.objectives.concerned_user && obj.objectives.type !== null
  );
};

const getMonthlyData = (data) => ({
  january: data?.january || 0,
  february: data?.february || 0,
  march: data?.march || 0,
  april: data?.april || 0,
  may: data?.may || 0,
  june: data?.june || 0,
  july: data?.july || 0,
  august: data?.august || 0,
  september: data?.september || 0,
  october: data?.october || 0,
  november: data?.november || 0,
  december: data?.december || 0,
});

export const getTeamObjRealized = (objectivesRealized, currentMonth) => {
  const teamTotals = calcTeamObjectives(objectivesRealized);
  const teamObjRealized = objectivesRealized.map((obj) =>
    getMonthlyData(obj?.realized)
  );
  const teamObjectivesTargeted = objectivesRealized.map((obj) =>
    getMonthlyData(obj?.objectives)
  );

  const newMissions = objectivesRealized.map((obj) =>
    getMonthlyData(obj?.new_missions_per_month)
  );

  return formatContentData({
    teamTotals,
    mgr_data: {
      teamObjRealized,
      teamObjectivesTargeted,
      newMissions,
    },
    currentMonth,
  });
};

const formatContentData = (content) => {
  const { teamTotals, mgr_data, currentMonth } = content;

  const formattedTeamTotals = {
    ...Object.keys(teamTotals).reduce((acc, month) => {
      acc[month] = {
        fontWeight: month === currentMonth ? "bold" : "normal",
        value: teamTotals[month],
        color: "default",
        new_missions_per_month: "",
      };
      return acc;
    }, {}),
  };

  const formattedMgrData = mgr_data.teamObjRealized.map((managerObj, index) => {
    const managerTargetedObj = mgr_data.teamObjectivesTargeted[index];

    const formattedManagerData = Object.keys(managerObj).reduce(
      (acc, month) => {
        const target = managerTargetedObj[month];
        const objectiveValue = managerObj[month];
        const missionValue = mgr_data.newMissions[index][month];

        acc[month] = {
          fontWeight: month === currentMonth ? "bold" : "normal",
          value: objectiveValue,
          new_missions_per_month:
            missionValue !== 0 ? `(${missionValue})` : `(${0})`,
          color: target <= objectiveValue ? "green" : "red",
        };
        return acc;
      },
      {}
    );

    return formattedManagerData;
  });

  const formattedData = [formattedTeamTotals, ...formattedMgrData];

  return formattedData;
};

const getPersonalObjRealized = (objectivesRealized, currentMonth) => {
  const personalObjRealized = objectivesRealized.map((obj) => obj?.realized);
  const personalObjectivesTargeted = objectivesRealized.map((obj) =>
    getMonthlyData(obj?.objectives)
  );
  const formatTargetedObj = personalObjectivesTargeted.map((obj) =>
    Object.keys(obj).reduce((acc, month) => {
      acc[month] = {
        value: obj[month],
        fontWeight: month === currentMonth ? "bold" : "normal",
      };
      return acc;
    }, {})
  );
  const formatRealized = Object.keys(personalObjRealized[0]).reduce(
    (acc, month) => {
      acc[month] = {
        fontWeight: month === currentMonth ? "bold" : "normal",
        value: personalObjRealized[0][month],
        color:
          personalObjRealized[0][month] >= personalObjectivesTargeted[0][month]
            ? colors.chateauGreen
            : colors.errorPopup,
      };
      return acc;
    },
    {}
  );

  return [formatTargetedObj[0], formatRealized];
};

export const formatObj = ({ objectivesRealized, scope, currentMonth }) => {
  return scope === SCOPES.MACRO
    ? getTeamObjRealized(objectivesRealized, currentMonth)
    : getPersonalObjRealized(objectivesRealized, currentMonth);
};

export const extractTeamNames = (objectives) => {
  return objectives.map((obj) => (
    <Typography
      key={obj?.person.id}
      type="span"
      label={
        <FormattedMessage
          id={`${obj?.person.first_name} ${obj?.person.last_name}`}
          defaultMessage={`${obj?.person.first_name} ${obj?.person.last_name}`}
        />
      }
    />
  ));
};

const extractMonthData = (data) => {
  return MONTHS.map((month) => data[month.toLowerCase()]);
};

export const calcTeamObjectives = (objectives) => {
  const filteredMonthData = objectives?.map((obj) =>
    extractMonthData(obj?.objectives)
  );
  const result = filteredMonthData?.reduce((previous, current) => {
    return previous.map((value, i) => value + current[i]);
  });

  const obj = {
    january: result[0] || 0,
    february: result[1] || 0,
    march: result[2] || 0,
    april: result[3] || 0,
    may: result[4] || 0,
    june: result[5] || 0,
    july: result[6] || 0,
    august: result[7] || 0,
    september: result[8] || 0,
    october: result[9] || 0,
    november: result[10] || 0,
    december: result[11] || 0,
  };
  return obj;
};
